import { useEffect, useState } from "react";
import { InputText } from 'primereact/inputtext'
import IconButton from "../../buttons/IconButton";
import { SaveIcon, PlusIcon, CopyIcon, CloseIcon } from "../../Icons";
import { Dropdown } from "primereact/dropdown";
import ParameterPresetChild from "./ParameterPresetChild";
import { BasicNameValue, MiscellaneousParameterList, StarCCMParameterList } from "../../../types/types";
import toast from "react-hot-toast";
import { useGetMiscParameters, useGetStarCCMParameters } from "../../../api/runconfig-API";


type ParameterPresetsProps = {
  presetType: string;
  currentPreset: StarCCMParameterList | MiscellaneousParameterList;
  presets: StarCCMParameterList[] | MiscellaneousParameterList[];
  readOnly?: boolean;
  updateCurrentSelectedPreset: (preset: StarCCMParameterList | MiscellaneousParameterList) => void;
  updateSelectedPreset?: (preset: StarCCMParameterList | MiscellaneousParameterList) => void;
};

const ParameterPresetManager: React.FC<ParameterPresetsProps> = ({
  presets, presetType, currentPreset, readOnly = false, updateSelectedPreset, updateCurrentSelectedPreset
}) => {
  const [, fetchStarccmParams] = useGetStarCCMParameters()
  const [, fetchMiscParams] = useGetMiscParameters()

  const [availablePresets, setAvailablePresets] = useState<StarCCMParameterList[] | MiscellaneousParameterList[]>([]);
  const [availableParams, setAvailableParams] = useState<string[]>([]);

  const [selectedPreset, setSelectedPreset] = useState<StarCCMParameterList | MiscellaneousParameterList>();

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const [presetName, setPresetName] = useState<string>("");

  const [parameterChildren, setParameterChildren] = useState<BasicNameValue[]>([]);
  const [currentPresetType, setCurrentPresetType] = useState<StarCCMParameterList | MiscellaneousParameterList>();

  useEffect(() => {
    if (presetType === "StarCCMParameterList") {
      setCurrentPresetType(new StarCCMParameterList());
    } else {
      setCurrentPresetType(new MiscellaneousParameterList());
    }
  }, [presetType]);

  useEffect(() => {
    setAvailablePresets(presets);
  }, [presets]);

  useEffect(() => {
    if (isEditing && parameterChildren.length === 0) {
      setParameterChildren(prev => [...prev, new BasicNameValue({
        name: '',
        value: ''
      })]);
    }
  }, [isEditing, parameterChildren?.length]);

  // Fetch StarCCM parameter list
  useEffect(() => {
    const fetchStarccmParamsList = async () => {
      const starccmParamsList = [];
      try {
        const starccmRes = await fetchStarccmParams();
        starccmRes.data.forEach((item) => {
          starccmParamsList.push(item);
        });
      } catch { }
      setAvailableParams(prev => Array.from(new Set([...starccmParamsList, ...prev])));
    };

    const fetchMiscParamsList = async () => {
      const miscList = [];
      try {
        const miscRes = await fetchMiscParams();
        miscRes.data.forEach((item) => {
          miscList.push(item);
        });
      } catch { }
      setAvailableParams(prev => Array.from(new Set([...miscList, ...prev])));
    };

    if (isEditing) {
      if (currentPresetType instanceof StarCCMParameterList) {
        fetchStarccmParamsList();
      } else if (currentPresetType instanceof MiscellaneousParameterList) {
        fetchMiscParamsList();
      }
    }
  }, [isEditing, currentPresetType]);

  useEffect(() => {
    if (currentPreset) {
      setSelectedPreset(currentPreset);
      setParameterChildren(currentPreset.paramList || []);
      setPresetName(currentPreset.name || "");
    }
  }, [currentPreset]);

  useEffect(() => { }, [parameterChildren]);

  const handleAddChild = (newChild: BasicNameValue) => {
    setParameterChildren(prev => [...prev, newChild]);
  };

  const handleDeleteChild = (index: number) => {
    setParameterChildren(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered;
    });
    setSelectedPreset(prev => {
      if (!prev) return prev;
      const updatedPreset = Object.assign(
        Object.create(Object.getPrototypeOf(prev)),
        { ...prev, paramList: prev.paramList?.filter((_, i) => i !== index) || [] }
      );
      return updatedPreset;
    });
  };

  // Adds a new parameter set to the current preset
  const handleAddParameter = (parameterName: string, index: number, value?: string) => {
    // Always add to availableParams if not present
    setAvailableParams(prev => {
      if (!parameterName || prev.includes(parameterName)) return prev;
      toast.success(`Added Parameter: ${parameterName}`);
      return [...prev, parameterName];
    });
    // Update value if provided
    if (value?.trim() !== "") {
      setParameterChildren(prev => {
        const updated = [...prev];
        updated[index].value = value;
        return updated;
      });
    }
  };

  const handleDuplicatePreset = () => {
    const dupParamList = selectedPreset?.paramList.map(param => new BasicNameValue({ name: param.name, value: param.value })) || [];
    const dupValues = {
      name: `${selectedPreset.name}_copy`,
      isPreset: selectedPreset.isPreset,
      paramList: dupParamList
    };
    const newPreset = currentPreset instanceof StarCCMParameterList ? new StarCCMParameterList(dupValues) : new MiscellaneousParameterList(dupValues);
    setIsEditing(true);
    setPresetName(newPreset.name || "");
    setParameterChildren(newPreset.paramList || []);
    setSelectedPreset(newPreset);
  }

  const handleSavePreset = () => {
    if (presetName.trim() !== "") {
      // Create the new preset object
      const newPreset = currentPresetType instanceof StarCCMParameterList
        ? new StarCCMParameterList({ name: presetName, paramList: parameterChildren })
        : new MiscellaneousParameterList({ name: presetName, paramList: parameterChildren });
      newPreset.paramList = newPreset.paramList.filter(param => param.name.trim() !== "" && param.value.trim() !== "");
      setAvailablePresets(prev => {
        // Remove any preset with the same name (edit/overwrite)
        const filtered = prev.filter(p => p.name !== newPreset.name);
        let updated: StarCCMParameterList[] | MiscellaneousParameterList[];
        if (prev.every(p => p instanceof StarCCMParameterList) && newPreset instanceof StarCCMParameterList) {
          updated = [...(filtered as StarCCMParameterList[]), newPreset];
        } else if (prev.every(p => p instanceof MiscellaneousParameterList) && newPreset instanceof MiscellaneousParameterList) {
          updated = [...(filtered as MiscellaneousParameterList[]), newPreset];
        } else if (prev.length === 0) {
          if (newPreset instanceof MiscellaneousParameterList) {
            updated = [newPreset] as MiscellaneousParameterList[];
          } else if (newPreset instanceof StarCCMParameterList) {
            updated = [newPreset] as StarCCMParameterList[];
          }
        } else {
          updated = prev;
        }

        return updated;
      });
      // Set selectedPreset to the actual reference in updated array

      updateSelectedPreset?.(newPreset);
      setSelectedPreset(newPreset);
      setParameterChildren(newPreset.paramList || []);
      // Refresh the list of available presets
      setAvailablePresets(prev => {
        const filtered = prev.filter(p => p.name !== newPreset.name);
        if (currentPresetType instanceof StarCCMParameterList && newPreset instanceof StarCCMParameterList) {
          return [...(filtered as StarCCMParameterList[]), newPreset];
        } else if (currentPresetType instanceof MiscellaneousParameterList && newPreset instanceof MiscellaneousParameterList) {
          return [...(filtered as MiscellaneousParameterList[]), newPreset];
        } else return prev;
      });
      setIsEditing(false);
    } else {
      toast.error("Preset Name Required!")
    }
  }

  return (
    <div className="flex flex-col items-center w-[100%] space-y-4">
      {/* Selector */}
      <div className="w-full grid grid-cols-9 gap-4">
        {
          isEditing ? (
            <InputText
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="New preset name"
              className="col-span-8"
            />
          ) : (
            <div className="col-span-8">
              <Dropdown
                pt={{
                  root: {
                    className: 'bg-transparent dark:border-neutral-70 dark:bg-transparent min-w-full'
                  },
                  input: {
                    className: 'py-2'
                  }
                }}
                disabled={readOnly}
                value={selectedPreset?.name || null}
                options={availablePresets?.map((p: StarCCMParameterList | MiscellaneousParameterList) => ({
                  label: p.name,
                  value: p.name,
                  preset: p
                }))}
                onChange={(e) => {
                  const selected = availablePresets.find(
                    (p: StarCCMParameterList | MiscellaneousParameterList) => p.name === e.value
                  );
                  setSelectedPreset(selected);
                  setParameterChildren(selected?.paramList || []);
                  updateCurrentSelectedPreset(selected);
                }}
                placeholder={selectedPreset?.name || "Select a preset"}
                emptyMessage="No presets available"
                emptyFilterMessage="No matching presets found"
                data-pr-tooltip="Select a preset to view"
                showClear
                filter
                filterPlaceholder="Search presets"
                panelClassName="min-w-full"
              />
            </div>
          )
        }
        {/* Buttons */}
        {
          isEditing ? (
            <div className="flex items-center space-x-2">
              <IconButton
                title="Save Preset"
                severity="info"
                icon={<SaveIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                onClick={() => handleSavePreset()}
              />
              <IconButton
                title="Cancel Editing"
                additionalClassName=" bg-gm-black"
                severity="danger"
                icon={<CloseIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                onClick={() => {
                  setIsEditing(false);
                  // Revert the state back based on the arguments passed
                  setSelectedPreset(currentPreset);
                  setPresetName(currentPreset?.name || "");
                  setParameterChildren(currentPreset?.paramList || []);
                }}
              />
            </div>
          ) : (
            readOnly ? null : (
              <div className="flex items-center space-x-2">
                <IconButton
                  title="Create New Preset"
                  severity="info"
                  icon={<PlusIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                  onClick={() => {
                    setIsEditing(true);
                    const now = new Date();
                    const pad = (n: number) => n.toString().padStart(2, '0');
                    const suffix = `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear().toString().slice(-2)}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
                    setPresetName(`NewPreset_${suffix}`);
                    setParameterChildren([new BasicNameValue({ name: '', value: '' })]);
                  }}
                />
                {
                  selectedPreset && (
                    <IconButton
                      title="Duplicate Preset"
                      severity="secondary"
                      icon={<CopyIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                      onClick={() => handleDuplicatePreset()}
                    />
                  )
                }
              </div>)
          )
        }
      </div>
      {/* Preset Population */}
      <div className="w-full flex flex-col items-stretch space-y-2">
        {parameterChildren.map((p, idx) => (
          <ParameterPresetChild
            key={`param-child-${idx}`}
            index={idx}
            paramType={currentPresetType.constructor.name}
            localAvailableParams={availableParams}
            currentParameter={p}
            onAdd={handleAddChild}
            onAddLocalParameter={handleAddParameter}
            onDelete={() => handleDeleteChild(idx)}
            canAdd={idx === parameterChildren.length - 1}
            canEdit={isEditing}
            canDelete={parameterChildren.length > 1}
          />
        ))}
      </div>
    </div>
  )
};

export default ParameterPresetManager;
